import Note from '@/components/Note'
import WebPreview from '@/components/WebPreview'
import { Button } from '@/components/ui/button'
import { ScrollArea } from '@/components/ui/scroll-area'
import { transformCustomEmojisInContent, trimTagEnd } from '@/lib/draft-event'
import { addPendingPublish, removePendingPublish, usePendingCountdown } from '@/lib/pendingPublish'
import { isTouchDevice, now } from '@/lib/utils'
import {
  getReplaceableCoordinateFromEvent,
  getRoot,
  isProtectedEvent,
  isReplaceableEvent,
  isReplyNoteEvent
} from '@/lib/event'
import { addReplies } from '@/atoms/replies'
import { checkLogin, useCurrentPubkey } from '@/atoms/session'
import client from '@/services/client.service'
import { discardPendingEvent, savePendingEvent } from '@/atoms/pending'

import { HugeiconsIcon } from '@hugeicons/react'
import {
  EyeOffIcon,
  ImageUpload01Icon,
  Loading01Icon,
  SmileIcon,
  SlowWindsIcon,
  ShieldMinus
} from '@hugeicons/core-free-icons'
import { NostrEvent } from '@nostr/tools/core'
import * as kinds from '@nostr/tools/kinds'
import { useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import EmojiPickerDialog from '../EmojiPickerDialog'
import PostTextarea, { TPostTextareaHandle } from './PostTextarea'
import Uploader from './Uploader'
import BlossomUploadChecklist from './BlossomUploadChecklist'
import {
  getImetaTagByUrl,
  type ServerStatus,
  type UploadFile
} from '@/services/media-upload.service'
import RelaySelector, { Item } from '../RelaySelector'
import { simplifyUrl } from '@/lib/url'
import { parse } from '@nostr/tools/nip27'
import { unique } from '@nostr/gadgets/utils'
import { invalidateFeed, invalidateReplies } from '@/atoms/invalidation'
import { unwrappedFavoriteRelaysWithSetsAtom } from '@/atoms/lists'
import { useAtom, useAtomValue } from 'jotai'
import { ResolvedSet } from '@nostr/gadgets/sets'
import { getCachedPostAtom } from './cache'

const PENDING_PUBLISH_MS = 10_000

function waitForAbortableDelay(duration: number, signal: AbortSignal) {
  return new Promise<void>((resolve, reject) => {
    if (signal.aborted) {
      reject(new DOMException('Aborted', 'AbortError'))
      return
    }

    const timeout = window.setTimeout(() => {
      signal.removeEventListener('abort', handleAbort)
      resolve()
    }, duration)

    const handleAbort = () => {
      window.clearTimeout(timeout)
      signal.removeEventListener('abort', handleAbort)
      reject(new DOMException('Aborted', 'AbortError'))
    }

    signal.addEventListener('abort', handleAbort)
  })
}

function PendingPublishToast({ endAt, onUndo }: { endAt: number; onUndo: () => void }) {
  const secondsLeft = usePendingCountdown(endAt)
  const { t } = useTranslation()

  return (
    <div className="flex items-center gap-3 rounded-full bg-primary text-primary-foreground shadow hover:bg-primary-hover py-2 pl-4 pr-3">
      <div className="text-sm font-medium">{t('Changed your mind?')}</div>
      <div className="text-sm font-medium w-8 text-center">{secondsLeft}s</div>
      <Button variant="secondary" size="sm" onClick={onUndo}>
        {t('Cancel')}
      </Button>
    </div>
  )
}

export default function PostContent({
  defaultContent = '',
  editingEvent,
  parentEvents,
  parentExternal,
  close,
  open,
  openFromRelay
}: {
  defaultContent?: string
  editingEvent?: NostrEvent
  parentEvents?: NostrEvent[]
  parentExternal?: string
  close: () => void
  open: (content?: string) => void
  openFromRelay?: string
}) {
  const { t } = useTranslation()
  const pubkey = useCurrentPubkey()
  const [text, setText] = useState('')
  const textareaRef = useRef<TPostTextareaHandle>(null)
  const [posting, setPosting] = useState(false)
  const [uploadProgresses, setUploadProgresses] = useState<
    { file: UploadFile; cancel: () => void; servers: { url: string; status: ServerStatus }[] }[]
  >([])

  const seenOn =
    parentEvents && parentEvents.every((pe) => isProtectedEvent(pe))
      ? parentEvents.flatMap((pe) => client.getSeenEventRelayUrls(pe.id, pe))
      : []

  const [cached, setCached] = useAtom(
    getCachedPostAtom(defaultContent, parentEvents, () => ({
      nsfw: false,
      protected: false,
      content: null,
      quietReply: false,
      relaySelection: openFromRelay
        ? [{ kind: 'relay', url: openFromRelay }]
        : seenOn.length
          ? seenOn.map((url) => ({ kind: 'relay', url }))
          : parentEvents
            ? ['ourWriteRelays', 'theirReadRelays'].map((id) => ({ kind: 'item', id, label: '' }))
            : [{ kind: 'item', id: 'ourWriteRelays', label: '' }]
    }))
  )

  const hasSpecificRelays = cached.relaySelection.some((item) => item.kind !== 'item')

  const canPost =
    !!pubkey &&
    !!text &&
    !posting &&
    !uploadProgresses.length &&
    (!cached.protected || hasSpecificRelays)

  const favorites = useAtomValue(unwrappedFavoriteRelaysWithSetsAtom)

  const initialSelected = useMemo(() => {
    if (openFromRelay) return new Set([openFromRelay])
    if (seenOn.length) return new Set(seenOn)
    if (parentEvents) return new Set(['ourWriteRelays', 'theirReadRelays'])
    return new Set(['ourWriteRelays'])
  }, [parentEvents])

  const relayOptions = useMemo(() => {
    let opts: Item[] = [{ kind: 'item', id: 'ourWriteRelays', label: t('Our outbox relays') }]

    let hasExplicitMentions = false
    if (!parentEvents) {
      for (const block of parse(text)) {
        if (block.type === 'reference') {
          hasExplicitMentions = true
          break
        }
      }
    }
    if (parentEvents || hasExplicitMentions) {
      opts.push({ kind: 'item', id: 'theirReadRelays', label: t('Their inbox relays') })
    }

    const favs = (openFromRelay ? [openFromRelay] : [])
      .concat(seenOn)
      .concat((favorites?.filter((f): f is string => typeof f === 'string') ?? []) as string[])
    if (favs.length) {
      opts.push({ kind: 'separator' })
      opts = opts.concat(
        unique(favs).map((url) => ({
          kind: 'relay',
          url
        }))
      )
    }

    const favoriteRelaySets = (favorites?.filter(
      (f): f is ResolvedSet<string> => typeof f !== 'string'
    ) ?? []) as ResolvedSet<string>[]
    if (favoriteRelaySets.length) {
      opts.push({ kind: 'separator' })
      for (const fav of favoriteRelaySets) {
        opts.push({
          kind: 'relay-set',
          identifier: fav.pointer.identifier,
          relays: fav.items,
          name: fav.title
        })
      }
    }

    return opts
  }, [parentEvents, favorites, openFromRelay])

  const relaySelectionDescription = useMemo(() => {
    if (cached.relaySelection.length === 0) {
      return t('No relays selected')
    }
    if (cached.relaySelection.length === 1) {
      const item = cached.relaySelection[0]
      if (item.kind === 'item') {
        if (item.id === 'ourWriteRelays') {
          return t('Our write relays')
        }
        if (item.id === 'theirReadRelays') {
          return t('Their read relays')
        }
      }

      if (item.kind === 'relay') {
        return simplifyUrl(item.url)
      }

      if (item.kind === 'relay-set') {
        return item.relays.length > 1
          ? t('{{count}} relays', { count: item.relays.length })
          : simplifyUrl(item.relays[0])
      }
    }

    const hasOurWriteRelays = cached.relaySelection.some(
      (item) => item.kind === 'item' && item.id === 'ourWriteRelays'
    )
    const hasTheirReadRelays = cached.relaySelection.some(
      (item) =>
        (item.kind === 'item' && item.id === 'theirReadRelays') ||
        (item.kind === 'relay' && item.url === 'theirReadRelays')
    )
    const relayCount = cached.relaySelection.reduce((count, item) => {
      if (item.kind === 'relay') {
        return count + 1
      }
      if (item.kind === 'relay-set') {
        return count + item.relays.length
      }
      return count
    }, 0)
    if (hasOurWriteRelays && hasTheirReadRelays) {
      return relayCount > 0
        ? t('Our write relays, their read relays and {{count}} other relays', { count: relayCount })
        : t('Our write relays and their read relays')
    }
    if (hasOurWriteRelays) {
      return relayCount > 0
        ? t('Our write relays and {{count}} other relays', { count: relayCount })
        : t('Our write relays')
    }
    if (hasTheirReadRelays) {
      return relayCount > 0
        ? t('Their read relays and {{count}} other relays', { count: relayCount })
        : t('Their read relays')
    }
    return t('{{count}} relays', { count: relayCount })
  }, [cached.relaySelection, t])

  return (
    <div className="space-y-2">
      {parentEvents && parentEvents.length > 1 ? (
        <ScrollArea className="flex max-h-48 flex-col overflow-y-auto rounded-lg border bg-muted/40">
          {parentEvents.map((evt) => (
            <div key={evt.id} className="p-2 sm:p-3 pointer-events-none border-b last:border-none">
              <Note size="small" event={evt} hideParentNotePreview showClientTag />
            </div>
          ))}
        </ScrollArea>
      ) : parentEvents && parentEvents.length === 1 ? (
        <ScrollArea className="flex max-h-48 flex-col overflow-y-auto rounded-lg border bg-muted/40">
          <div className="p-2 sm:p-3 pointer-events-none">
            <Note size="small" event={parentEvents[0]} hideParentNotePreview showClientTag />
          </div>
        </ScrollArea>
      ) : parentExternal ? (
        <div className="pointer-events-none rounded-lg border bg-muted/40 overflow-hidden">
          <WebPreview url={parentExternal} />
        </div>
      ) : null}
      <PostTextarea
        ref={textareaRef}
        text={text}
        setText={setText}
        defaultContent={defaultContent}
        parentEvents={parentEvents}
        onSubmit={() => post()}
        className="min-h-52"
        onUploadStart={handleUploadStart}
        onUploadEnd={handleUploadEnd}
        onUploadInit={handleUploadInit}
        onServerStatus={handleServerStatus}
      />
      {uploadProgresses.length > 0 && (
        <div className="space-y-2">
          {uploadProgresses.map(({ file, cancel, servers }, index) => (
            <BlossomUploadChecklist
              key={`${file.name}-${index}`}
              fileName={file.name}
              servers={servers}
              onCancel={() => {
                cancel?.()
                handleUploadEnd(file)
              }}
            />
          ))}
        </div>
      )}
      <div className="flex items-center gap-2">
        {t('Post to')}
        <RelaySelector
          selected={initialSelected}
          onChange={(rs) => setCached((curr) => ({ ...curr, relaySelection: rs }))}
          items={relayOptions}
          hideChangedDot
        >
          <Button type="button" variant="outline" className="px-2 flex-1 max-w-fit justify-start">
            {relaySelectionDescription}
          </Button>
        </RelaySelector>
      </div>
      <div className="flex items-center justify-between">
        <div className="flex gap-2 items-center">
          <Uploader
            onUploadSuccess={({ url }) => {
              textareaRef.current?.appendText(url, true)
            }}
            onUploadStart={handleUploadStart}
            onUploadEnd={handleUploadEnd}
            onUploadInit={handleUploadInit}
            onServerStatus={handleServerStatus}
            accept="image/*,video/*,audio/*"
          >
            <Button variant="ghost" size="icon">
              <HugeiconsIcon icon={ImageUpload01Icon} />
            </Button>
          </Uploader>
          {/* I'm not sure why, but after triggering the virtual keyboard,
              opening the emoji picker drawer causes an issue,
              the emoji I tap isn't the one that gets inserted. */}
          {!isTouchDevice() && (
            <EmojiPickerDialog
              onEmojiClick={(emoji) => {
                if (!emoji) return
                textareaRef.current?.insertEmoji(emoji)
              }}
            >
              <Button variant="ghost" size="icon">
                <HugeiconsIcon icon={SmileIcon} />
              </Button>
            </EmojiPickerDialog>
          )}
          <Button
            variant="ghost"
            size="icon"
            title={t('NSFW')}
            className={cached.nsfw ? 'bg-primary/15 text-primary hover:bg-primary/15' : ''}
            onClick={() => setCached((curr) => ({ ...curr, nsfw: !curr.nsfw }))}
          >
            <HugeiconsIcon icon={EyeOffIcon} />
          </Button>
          {parentEvents && parentEvents.length > 0 && (
            <Button
              variant="ghost"
              size="icon"
              title={
                parentEvents[0].kind !== kinds.ShortTextNote ? t('Comment reply') : t('Quiet reply')
              }
              className={
                parentEvents[0].kind !== kinds.ShortTextNote ||
                parentEvents.length > 1 ||
                cached.quietReply
                  ? 'bg-primary/15 text-primary hover:bg-primary/15'
                  : ''
              }
              onClick={() => {
                if (parentEvents[0].kind !== kinds.ShortTextNote || parentEvents.length > 1)
                  // when replying to anything other than a kind:1
                  // or when replying to multiple events at the same time
                  // force the "quiet reply"/comment
                  return

                setCached((curr) => ({ ...curr, quietReply: !curr.quietReply }))
              }}
            >
              <HugeiconsIcon icon={SlowWindsIcon} />
            </Button>
          )}
          <Button
            variant="ghost"
            size="icon"
            title={t('Protected (NIP-70)')}
            className={cached.protected ? 'bg-primary/15 text-primary hover:bg-primary/15' : ''}
            onClick={() => setCached((curr) => ({ ...curr, protected: !curr.protected }))}
          >
            <HugeiconsIcon icon={ShieldMinus} />
          </Button>
        </div>
        <div className="flex gap-2 items-center">
          <div className="flex gap-2 items-center max-sm:hidden">
            <Button variant="secondary" disabled={!canPost || posting} onClick={saveForLater}>
              {editingEvent ? t('Save') : t('Save for later')}
            </Button>
            <Button
              variant="secondary"
              onClick={(e) => {
                e.stopPropagation()
                close()
              }}
            >
              {t('Cancel')}
            </Button>
            <Button type="submit" disabled={!canPost} onClick={post}>
              {posting && <HugeiconsIcon icon={Loading01Icon} className="animate-spin" />}
              {parentEvents ? t('Reply') : t('Post')}
            </Button>
          </div>
        </div>
      </div>
      <div className="flex flex-col gap-2 sm:hidden">
        <div className="flex gap-2">
          <Button
            className="w-full"
            variant="secondary"
            disabled={!canPost || posting}
            onClick={saveForLater}
          >
            {editingEvent ? t('Save') : t('Save for later')}
          </Button>
          <Button
            className="w-full"
            variant="secondary"
            onClick={(e) => {
              e.stopPropagation()
              close()
            }}
          >
            {t('Cancel')}
          </Button>
        </div>
        <Button className="w-full" type="submit" disabled={!canPost} onClick={post}>
          {posting && <HugeiconsIcon icon={Loading01Icon} className="animate-spin" />}
          {parentEvents ? t('Reply') : t('Post')}
        </Button>
      </div>
    </div>
  )

  async function prepare(): Promise<[event: NostrEvent, relayUrls: string[]]> {
    const shouldPublishAsComment =
      !!parentEvents &&
      (parentEvents[0].kind !== kinds.ShortTextNote ||
        cached.quietReply ||
        (parentEvents && parentEvents.length > 1))

    const tags: string[][] = []
    function addTag(tag: string[]) {
      for (let i = 0; i < tags.length; i++) {
        const t = tags[i]
        if (t[0] === tag[0] && t[1] === tag[1]) return
      }
      tags.push(tag)
    }

    const additionalMentionPubkeys: string[] = []
    function addMention(pk: string) {
      if (additionalMentionPubkeys.includes(pk)) return
      additionalMentionPubkeys.push(pk)
    }

    const { content, emojiTags } = transformCustomEmojisInContent(text)
    for (const emoji of emojiTags) addTag(emoji)

    for (const block of parse(content)) {
      switch (block.type) {
        case 'image':
          const tag = getImetaTagByUrl(block.url)
          if (tag) addTag(tag)
          break
        case 'hashtag':
          addTag(['t', block.value])
          break
        case 'reference':
          if ('identifier' in block.pointer) {
            addTag([
              'q',
              `${block.pointer.kind}:${block.pointer.pubkey}:${block.pointer.identifier}`
            ])
          } else if ('id' in block.pointer) {
            const url = client.getEventHint(block.pointer.id)
            addTag(['q', block.pointer.id, url])
          } else {
            addTag(['p', block.pointer.pubkey])
            addMention(block.pointer.pubkey)
          }
          break
      }
    }

    if (parentEvents) {
      // root tags
      for (const pe of parentEvents) {
        const rootInfo = await getRoot(pe)

        if (shouldPublishAsComment) {
          // NIP-22
          if (rootInfo && 'identifier' in rootInfo) {
            addTag([
              'A',
              `${rootInfo.kind}:${rootInfo.pubkey}:${rootInfo.identifier}`,
              ...(rootInfo.relays?.[0] ? [rootInfo.relays?.[0]] : [])
            ])
            addTag(['K', `${rootInfo.kind}`])
            addTag(['P', rootInfo.pubkey])
          } else if (rootInfo && 'id' in rootInfo) {
            addTag(['E', rootInfo.id, ...(rootInfo.relays?.[0] ? [rootInfo.relays?.[0]] : [])])
            if (rootInfo.kind) addTag(['K', `${rootInfo.kind}`])
            if (rootInfo.author) addTag(['P', rootInfo.author])
          } else if (rootInfo && 'value' in rootInfo) {
            addTag(['I', rootInfo.value])
            if (rootInfo.value.startsWith('http')) addTag(['K', 'web'])
          } else {
            // root === parent
            addTag(['E', pe.id])
            addTag(['K', `${pe.kind}`])
            addTag(['P', pe.pubkey])
          }
        } else {
          // NIP-10
          if (rootInfo && 'identifier' in rootInfo) {
            addTag([
              'a',
              `${rootInfo.kind}:${rootInfo.pubkey}:${rootInfo.identifier}`,
              ...(rootInfo.relays?.[0] ? [rootInfo.relays?.[0]] : []),
              'root'
            ])
            addMention(rootInfo.pubkey)
          } else if (rootInfo && 'id' in rootInfo) {
            addTag([
              'e',
              rootInfo.id,
              ...(rootInfo.relays?.[0] ? [rootInfo.relays?.[0]] : []),
              'root',
              ...(rootInfo.author ? [rootInfo.author] : [])
            ])
          } else {
            // root === parent (ignore external)
            addTag(['e', pe.id, client.getEventHint(pe.id)])
          }
        }
      }

      // parent tags
      for (const pe of parentEvents) {
        if (shouldPublishAsComment) {
          // NIP-22
          tags.push(
            ...[
              isReplaceableEvent(pe.kind)
                ? trimTagEnd([
                    'a',
                    getReplaceableCoordinateFromEvent(pe),
                    client.getEventHint(pe.id)
                  ])
                : trimTagEnd(['e', pe.id, client.getEventHint(pe.id), pe.pubkey]),
              ['k', `${pe.kind}`]
            ]
          )
        } else {
          // NIP-10
          tags.push(
            ...[
              isReplaceableEvent(pe.kind)
                ? ['a', getReplaceableCoordinateFromEvent(pe), client.getEventHint(pe.id), 'reply']
                : ['e', pe.id, client.getEventHint(pe.id), 'reply', pe.pubkey]
            ]
          )
        }
        addMention(pe.pubkey)
      }
    } else if (parentExternal) {
      // in this case it will always be NIP-22
      addTag(['I', parentExternal])
      if (parentExternal.startsWith('http')) addTag(['K', 'web'])
      addTag(['i', parentExternal])
      if (parentExternal.startsWith('http')) addTag(['k', 'web'])
    }

    for (const p of additionalMentionPubkeys.values()) addTag(['p', p])
    if (cached.nsfw) tags.push(['content-warning', 'NSFW'])
    if (cached.protected) tags.push(['-'])

    const newEvent = await client.signer?.signEvent({
      kind: shouldPublishAsComment ? kinds.Comment : kinds.ShortTextNote,
      content: content,
      tags,
      created_at: now()
    })
    if (!newEvent) throw new Error('no signer connected')

    const relayUrls = await client.determineTargetRelays(newEvent, {
      additionalRelayUrls: cached.relaySelection.flatMap((item) =>
        item.kind === 'relay' ? item.url : item.kind === 'relay-set' ? item.relays : []
      ),
      includeOurWriteRelays:
        cached.relaySelection.some(
          (item) => item.kind === 'item' && item.id === 'ourWriteRelays'
        ) || false,
      includeTheirReadRelays:
        cached.relaySelection.some(
          (item) => item.kind === 'item' && item.id === 'theirReadRelays'
        ) || false,
      additionalMentionPubkeys
    })

    return [newEvent, relayUrls]
  }

  async function post(e?: React.MouseEvent) {
    e?.stopPropagation()
    checkLogin(async () => {
      if (!canPost) return

      setPosting(true)
      try {
        const [newEvent, relayUrls] = await prepare()

        if (editingEvent) {
          discardPendingEvent(editingEvent.id)
        }

        client.addEventToCache(newEvent)
        client.emitEphemeralEvent(newEvent)
        addReplies([newEvent])
        const submittedText = text
        close()

        const abortController = new AbortController()
        const toastId = `pending-publish-${newEvent.id}`
        const endAt = Date.now() + PENDING_PUBLISH_MS
        const undoPublish = () => {
          abortController.abort()
          void client.removeEventFromCache(newEvent.id)
          client.emitEventRemoved(newEvent.id)
          ;(isReplyNoteEvent(newEvent) ? invalidateReplies : invalidateFeed)()
          toast.dismiss(toastId)
          removePendingPublish(newEvent.id)
          open(submittedText)
        }

        addPendingPublish(newEvent.id, endAt, undoPublish)
        toast(<PendingPublishToast endAt={endAt} onUndo={undoPublish} />, {
          id: toastId,
          duration: PENDING_PUBLISH_MS,
          className: '!bg-transparent !border-none !shadow-none !p-0',
          style: { '--width': 'fit-content' } as React.CSSProperties
        })

        void (async () => {
          try {
            await waitForAbortableDelay(PENDING_PUBLISH_MS, abortController.signal)
            toast.dismiss(toastId)
            removePendingPublish(newEvent.id)
            await client.publishEvent(relayUrls, newEvent)
            toast.success(t('Post successful'), { duration: 2000 })
          } catch (error) {
            toast.dismiss(toastId)
            removePendingPublish(newEvent.id)

            if (abortController.signal.aborted) {
              return
            }

            savePendingEvent(newEvent)
            console.warn('saved as pending')
            toast.error(t('Saved as pending'))
            const errors = error instanceof AggregateError ? error.errors : [error]
            for (const err of errors) {
              toast.error(
                `${t('Failed to post')}: ${err instanceof Error ? err.message : String(err)}`,
                { duration: 10_000 }
              )
              console.error(err)
            }
          }
        })()
      } catch (error) {
        const errors = error instanceof AggregateError ? error.errors : [error]
        for (const err of errors) {
          toast.error(
            `${t('Failed to post')}: ${err instanceof Error ? err.message : String(err)}`,
            { duration: 10_000 }
          )
          console.error(err)
        }
        return
      } finally {
        setPosting(false)
      }
    })
  }

  async function saveForLater(e?: React.MouseEvent) {
    e?.stopPropagation()
    checkLogin(async () => {
      if (!canPost) return

      setPosting(true)
      try {
        const [newEvent, _] = await prepare()

        if (editingEvent) {
          discardPendingEvent(editingEvent.id)
        }

        savePendingEvent(newEvent)
        close()
        toast.success(t('Saved for later'))
      } catch (error) {
        console.warn(
          'failed to save draft:',
          error instanceof Error ? error.message : String(error)
        )
        toast.error(
          `${t('Failed to save draft')}: ${error instanceof Error ? error.message : String(error)}`
        )
      } finally {
        setPosting(false)
      }
    })
  }

  function handleUploadStart(file: UploadFile, cancel: () => void) {
    setUploadProgresses((prev) => {
      const cleaned = prev.filter((item) => {
        if (item.file._uploadId === file._uploadId) return false
        if (item.file.name !== file.name) return true
        return !item.servers.every((s) => s.status.state === 'error')
      })
      return [...cleaned, { file, cancel, servers: [] }]
    })
  }

  function handleUploadInit(info: { file: UploadFile; cancel: () => void; servers: string[] }) {
    setUploadProgresses((prev) =>
      prev.map((item) =>
        item.file._uploadId === info.file._uploadId
          ? {
              ...item,
              servers: info.servers.map((url) => ({
                url,
                status: { state: 'pending' } as ServerStatus
              }))
            }
          : item
      )
    )
  }

  function handleServerStatus(file: UploadFile, server: string, status: ServerStatus) {
    setUploadProgresses((prev) =>
      prev.map((item) =>
        item.file._uploadId === file._uploadId
          ? {
              ...item,
              servers: item.servers.map((s) => (s.url === server ? { ...s, status } : s))
            }
          : item
      )
    )
  }

  function handleUploadEnd(file: UploadFile) {
    setUploadProgresses((prev) => prev.filter((item) => item.file._uploadId !== file._uploadId))
  }
}
